<template>
  <div id="table" class="dataTables_wrapper dt-bootstrap4 no-footer">
    <div
      class="font-size-h4 font-w600 p-2 mb-4 border-left border-4x border-primary bg-body-light"
      v-if="lastSearch"
    >
      <span class="text-primary font-w700" v-if="!loading">{{
        response.total
      }}</span>
      results found for
      <mark
        class="text-danger"
        style="cursor: pointer;"
        v-on:click="lastSearchClicked"
        >{{ lastSearch }}
      </mark>
    </div>
    <div class="table-responsive" style="overflow: initial;">
      <v-data-table
        v-model="selectedForEdit"
        :headers="showHeaders"
        :items="showItems"
        :items-per-page="filters.display"
        :options.sync="options"
        :server-items-length="response.total"
        :loading="loading"
        :footer-props="footerProps"
        :show-select="editMode && showItems.length > 0"
        mobile-breakpoint="0"
      >
        <template v-slot:body="{ items }">
          <tbody v-if="items.length > 0">
            <tr
              v-for="(item, idx) in items"
              :class="{ 'total-row': item.totalRow }"
              @click="rowClicked(item)"
              :key="idx"
            >
              <td v-if="editMode && (lastRowCheckable || idx != items.length - 1)">
                <v-checkbox class="m-0 p-0" v-model="selectedForEdit" :value="item" />
              </td>
              <td v-if="editMode && (!lastRowCheckable && idx == items.length - 1)">&nbsp;</td>

              <template v-for="header in headers">
                <template v-if="checkedColumns.includes(header.value)">
                  <!-- Hierarchy -->
                  <td
                    v-if="header.format === 'hierarchy'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <a :href="getCorrectLink(header.link, item, header)">
                      <template v-for="(l, idx) in parseInt(item.level)">
                        <i
                          v-if="l > 0"
                          class="fa fa-long-arrow-alt-right"
                          :key="idx"
                        ></i
                        >&nbsp;
                      </template>
                      {{ item[header.value] }}
                    </a>
                  </td>

                  <!-- Link -->
                  <td
                    v-else-if="header.format === 'link'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <span
                      v-if="
                        !!(tableSettings && tableSettings.lockSuperAdmin) &&
                          item.partner_id === 0
                      "
                      data-toggle="popover"
                      title="Super Admin Feature"
                      trigger="hover"
                      data-content="Only A Super Admin Can Edit This."
                    >
                      <span>{{ item[header.value] }}</span>
                      <i class="si si-lock ml-2"></i>
                    </span>
                    <a
                      v-else
                      :href="getCorrectLink(header.link, item, header)"
                      >{{ item[header.value] }}</a
                    >
                  </td>
                    <td
                        v-else-if="header.format === 'download'"
                        :key="header.value"
                        :class="getCellAlignmentClass(header)"
                    >
                    <span
                        data-toggle="popover"
                        :title="header.name"
                        trigger="hover"
                        data-content="Download all generated reports"
                    >
                        <a :href="getCorrectLink(header.link, item, header)">
                            <v-icon>mdi-download</v-icon>
                        </a>
                    </span>
                    </td>

                  <!-- dateTime -->
                  <td
                    v-else-if="header.format === 'dateTime'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{
                      item[header.value] &&
                      item[header.value] != '0000-00-00 00:00:00'
                        ? formatDate(
                            item[header.value],
                            timezone,
                            'YYYY-MM-DD hh:mm a'
                          )
                        : ''
                    }}
                  </td>

                  <!-- yesNo -->
                  <td
                    v-else-if="header.format === 'yesNo'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{
                      item[header.value] &&
                      item[header.value] != '0000-00-00 00:00:00'
                        ? 'Yes'
                        : 'No'
                    }}
                  </td>

                  <!-- nameLink -->
                  <td
                    v-else-if="header.format === 'nameLink'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <div v-for="sub in item[header.value]" :key="sub.id">
                      <a
                        :href="
                          `/admin/${item.partner_id}/${fixKey(header.value)}/${
                            sub.id
                          }`
                        "
                        >{{ sub.name }}</a
                      >
                    </div>
                  </td>

                  <!-- translate -->
                  <td
                    v-else-if="header.format === 'translate'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{ header.translation[item[header.value]] }}
                  </td>

                  <!-- items -->
                  <td
                    v-else-if="header.format === 'items'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <div
                      v-for="(pkg, idx) in item.packages"
                      :key="`package-${idx}-${pkg.package_id}`"
                    >
                      <a
                        :href="`/admin/${partner.id}/package/${pkg.package_id}`"
                        >{{ pkg.name }}</a
                      >
                    </div>
                    <div
                      v-for="(deal, idx) in item.deals"
                      :key="`deal-${idx}-${deal.deal_id}`"
                    >
                      <a :href="`/admin/${partner.id}/deal/${deal.deal_id}`">{{
                        deal.name
                      }}</a>
                    </div>
                    <div
                      v-for="(product, idx) in item.products"
                      :key="`product-${idx}-${product.product_id}`"
                    >
                      <a
                        :href="
                          `/admin/${partner.id}/product/${product.product_id}`
                        "
                        >{{ product.name }}</a
                      >
                    </div>
                  </td>

                  <!-- Campaign Invites -->
                  <!-- inDeal -->
                  <td
                    v-else-if="header.format === 'inDeal'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <div
                      v-for="(product, idx) in item.products"
                      :key="`product-inDeal-${idx}-${product.product_id}`"
                    >
                      {{ product.inDeal }}
                    </div>
                  </td>

                  <!-- Product Actions - Un-invite Product(s) -->
                  <td
                    v-else-if="header.text === 'Product Actions'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <div v-for="(product, idx) in item.products" :key="idx">
                      <button
                        class="btn btn-sm btn-danger js-tooltip-enabled mb-1"
                        :key="`product-actions-${idx}-${product.product_id}`"
                        tooltip="Uninvite Product"
                        @click.prevent="
                          $emit('uninvite', 'product', product.id)
                        "
                        :disabled="product.inDeal === 'Yes'"
                      >
                        <i class="fa fa-times-circle"></i>
                      </button>
                    </div>
                  </td>

                  <!-- Venue Actions - Un-invite Product(s) -->
                  <td
                    v-else-if="header.text === 'Venue Actions'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <div class="d-flex full-width justify-content-end">
                      <button
                        v-if="item.campaignStatus === 'Invited'"
                        class="btn btn-sm btn-warning js-tooltip-enabled mb-1 mr-1"
                        tooltip="Resend Invite Email"
                        @click.prevent="$emit('send-invite-email', [item.id])"
                        :disabled="inviteEmailsSending"
                      >
                        <i class="fa fa-envelope"></i>
                      </button>
                      <button
                        v-if="item.campaignStatus !== 'Decline'"
                        class="btn btn-sm btn-info js-tooltip-enabled mb-1 mr-1"
                        tooltip="Invite Product"
                        @click.prevent="$emit('invite', 'products', item.id)"
                        data-toggle="modal"
                        data-target="#inviteModalProducts"
                      >
                        <i class="fa fa-arrow-circle-left"></i>
                      </button>
                      <button
                        v-if="item.campaignStatus !== 'Decline'"
                        class="btn btn-sm btn-danger js-tooltip-enabled mb-1"
                        tooltip="Decline Venue Invite"
                        @click.prevent="$emit('uninvite', 'venue', item.id)"
                      >
                        <i class="fa fa-times-circle"></i>
                      </button>
                    </div>
                  </td>
                    <!-- Generic Actions -->
                    <td
                        v-else-if="header.format === 'actions'"
                        :key="header.value"
                        :class="getCellAlignmentClass(header)"
                    >
                        <div style="display: flex; justify-content: center">
                            <div v-for="(action, idx) in header.actions" :key="idx">
                                <v-btn
                                    class="mx-2"
                                    small
                                    color="primary"
                                    :key="`actions-${idx}-${item.id}`"
                                    @click="executeGenericAction(action, item)"
                                    :disabled="action.disabled(item) || action.loading"
                                >
                                    <v-icon dark>
                                        {{action.icon}}
                                    </v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </td>

                  <!-- upperCase -->
                  <td
                    v-else-if="header.format === 'upperCase'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{ Bandwango.ucwords(item[header.value]) }}
                  </td>

                  <!-- countItems -->
                  <td
                    v-else-if="header.format === 'countItems'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{
                      item[header.value] && Array.isArray(item[header.value])
                        ? item[header.value].length
                        : 0
                    }}
                  </td>

                  <!-- Truncate -->
                  <td
                    v-else-if="header.format === 'Truncate'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{ item[header.value] | truncate(header['var1']) }}
                  </td>

                  <!-- Currency -->
                  <td
                    v-else-if="header.format === 'currency'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{ item[header.value] | money(currencyPrefix) }}
                  </td>

                  <!-- Phone -->
                  <td
                    v-else-if="header.format === 'phone'"
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    {{ item[header.value] | phone }}
                  </td>

                  <!-- default -->
                  <td
                    v-else
                    :key="header.value"
                    :class="getCellAlignmentClass(header)"
                  >
                    <span v-if="header.format=='html'" v-html="item[header.value]"></span>
                    <span v-else>{{item[header.value]}}</span>
                  </td>
                </template>
              </template>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td :colspan="showHeaders.length" class="text-center">
                No Results.
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
export default {
  Vuetify: window.Vuetify,
  props: {
    tableSettings: {
      type: Object,
      required: true
    },
    filters: {
      type: Object,
      default() {
        return {}
      }
    },
    partner: {
      type: Object,
      required: false,
      default() {return {};}
    },
    columnOptions: {
      type: Object
    },
    checkedColumns: {
      type: Array
    },
    timezone: {
      type: String
    },
    response: {
      type: Object,
      default() {
        return {
          data: [],
          total: 0,
          from: 1,
          to: 0,
          current_page: 1
        }
      }
    },
    url: {
      type: String
    },
    editMode: {
      type: Boolean
    },
    massEdit: {
      type: Boolean,
      required: false
    },
    massEditSelections: {
      type: Array,
      default() {
        return []
      }
    },
    inviteEmailsSending: {
      type: Boolean
    },
    lastRowCheckable: {
        type: Boolean,
        default: true,
        required: false
    }
  },
  data() {
    return {
      headers: [],
      loading: true,
      options: {},
      columnSelect: false,
      lastSearch: '',
      lastSearchType: '',
      firstLoad: true,
    }
  },
  created() {
    this.setupHeaders()
  },
  computed: {
    selectedForEdit: {
      get() {
        return this.showItems.filter(
          item => this.massEditSelections.findIndex(s => s === item.id) >= 0
        )
      },
      set(v) {
        this.$emit(
          'selected-for-edit',
          v.map(o => o.id)
        )
      }
    },
    currencyPrefix() {
      return this.partner?.currency?.prefix || '$';
    },
    showHeaders() {
      return this.headers.filter(header => {
        return this.checkedColumns.includes(header.value) // !header.unchecked || // Jared: removed this because it was stopping filters form hiding header of column
      })
    },
    showItems() {
      return this.response.data
    },
    footerProps() {
      const perPageOptions = [20, 50, 100, 200]
      return {
        itemsPerPageOptions: this.editMode
          ? [...perPageOptions, 1000]
          : perPageOptions,
        showCurrentPage: true
      }
    }
  },
  watch: {
    options: {
      handler(newOptions, originalOptions) {
        let pageChange = false
        if ((originalOptions.page && newOptions.page != originalOptions.page) || (originalOptions.itemsPerPage && originalOptions.itemsPerPage != newOptions.itemsPerPage))
          pageChange = true;

        if( !this.firstLoad )
          this.getData(pageChange)
      },
      deep: true
    },
    columnOptions: {
      handler() {
        this.setupHeaders()
      },
      deep: true
    },
    editMode(val) {
      this.options.itemsPerPage = val && this.response.total || 20;
      this.getData();
    },
  },
  methods: {
    setupHeaders() {
      this.headers = Object.keys(this.columnOptions).map(key => {
        const option = this.columnOptions[key];
        if( !option.dependentFilter || ((Array.isArray(this.filters[option.dependentFilter]) && this.filters[option.dependentFilter].length) || (!Array.isArray(this.filters[option.dependentFilter]) && this.filters[option.dependentFilter]) )) {
          return {
            align: option.hasOwnProperty('align') ? option.align : 'start',
            filterable: option.hasOwnProperty('filterable')
                    ? option.filterable
                    : true,
            format: option.hasOwnProperty('format') ? option.format : null,
            sortable: !!option.hasOwnProperty('sortable'),
            unchecked: !!option.hasOwnProperty('unchecked'),
            translation: option.hasOwnProperty('translation')
                    ? option.translation
                    : [],
            link: option.link,
            superAdmin: option.superAdmin,
            text: option.name,
            value: key,
            content: option.content,
            actions: option.hasOwnProperty('actions') ? option.actions : null,
          }
        }
      }).filter(x => !!x);
      this.$forceUpdate()
    },
    getCorrectLink(linkStr, rowData, columnOption) {
      if (!linkStr) return rowData[columnOption.value]

      let splitStr
      if (linkStr.includes('+row.')) splitStr = linkStr.split('+row.')
      else splitStr = linkStr

      let cleanStr = splitStr[0].replace(/\'/g, '') //"/admin/venue/"
      let dataStr = splitStr[1].split('+') // 'id'
      let data = rowData[dataStr[0]] // gives back the actual ID of the row
      let str = cleanStr.concat(data) // concats the id with "/admin/venue/" + ID
      if (dataStr[1]) str = str.concat(encodeURI(dataStr[1].replace(/\'/g, '')))

      //after the url is created were adding the partnerid.
      if (columnOption.superAdmin && rowData.partner_id) {
        let split = str.split('/')
        split.splice(2, 0, `${rowData.partner_id}`)
        str = split.join('/')
      }
      return str
    },
    onPaginate() {
      this.applyData()
      window.scrollTo(0, 0)
    },
    formatDate(date, timezone, format = false) {
      if (!date) return ''

      let d = moment.utc(date)
      if (timezone) d = d.tz(timezone)

      if (format) {
        return d.format(format)
      }

      return d.toDate()
    },
    fixKey(key) {
      // replicates php function at top
      // make key non plural
      let keyLength = key.length
      if (key.substring(keyLength - 3) === 'ies') return key.slice(0, -3) + 'y'
      else if (key.substring(keyLength - 1) === 's') return key.slice(0, -1)

      return key
    },
    getData: function(pageChange = false) {
      this.firstLoad = false;

      // check if we need to change headers
      const toggleHeaders = Object.keys(this.columnOptions).reduce((c, t) => {
          return c || !!this.columnOptions[t];
      }, false);
      if( toggleHeaders )
        this.setupHeaders();

      // check if the parent has the response obj
      if (!this.url) {
        if (pageChange) {
            this.$emit('page-change', this.options)
        }
        return
      }

      const self = this
      const urlParams = new URLSearchParams(window.location.search)
      self.loading = true

      this.response.data = [];
      this.response.total = 0; //reset count

      const filters = {
        ...this.filters,
        page: this.options.page > 1 ? this.options.page : 1,
        display: this.options.itemsPerPage,
        sortBy:
          this.options.sortBy && this.options.sortBy.length > 0
            ? this.options.sortBy[0]
            : undefined,
        sortDesc:
          this.options.sortDesc && this.options.sortDesc.length > 0
            ? this.options.sortDesc[0]
            : undefined
      }

      const request = {
        ...filters,
        search: urlParams.get('search'),
        searchType: urlParams.get('type')
      }

      $.ajax({
        url: this.url,
        type: 'GET',
        data: request,
        dataType: 'json',

        success: function(response) {
            //self.response = response; // dont mutate parent object
            Object.keys(response).forEach((k) => {
              self.response[k] = response[k];
            })
            if (self.tableSettings.type === 'hierarchy')
              self.columnOptions.name.format = 'hierarchy'

            self.loading = false;
        },
        error: function(response) {
          Bandwango.helpers('swal', {
            title: 'Error!',
            text: response.responseText,
            icon: 'error'
          })
          self.loading = false
        }
      })
    },
    applyData: function() {
      this.$emit('apply-data')
    },
    lastSearchClicked: function() {
      this.search = this.lastSearch
      this.searchType = this.lastSearchType
      this.$emit('set-last-search', this.lastSearch, this.lastSearchType)
    },
    updateLastSearch(lastSearch, lastSearchType) {
      this.lastSearch = lastSearch
      this.lastSearchType = lastSearchType
    },
    resetPage(page) {
      this.options.page = page;
    },
    searchData(lastSearch, lastSearchType) {
      this.updateLastSearch(lastSearch, lastSearchType)
    },
    updatePerPage(v) {
      this.$emit('update-per-page', v)
    },
    getCellAlignmentClass(header) {
      return `text-${header.align ? header.align : 'start'}`
    },
    rowClicked(item) {
      this.$emit('row-clicked', item)
    },
    async executeGenericAction(action, item) {
      if(action.loading)
        return;
      if (action.confirmMessage && !confirm(action.confirmMessage)) {
          return false;
      }
      action.loading = true;
      try {
          //emit event from action with action and item as parameter
          this.$emit(action.event, action, item);
          if(action.successMessage){
              One.helpers('notify', {
                  align: 'center', type: 'success', icon: 'fa fa-info mr-1',
                  message: action.successMessage,
              });
          }
          action.loading = false;
          return true;
      } catch (e) {
          console.error(e);
          const message = action.errorMessage || action.message || 'An error occurred while processing your request';
          One.helpers('notify', {align: 'center', type: 'danger', icon: 'fa fa-times mr-1', message});
      }
      action.loading = false;
    }
  },
  mounted() {
    this.$nextTick(() => {
      Object.keys(this.columnOptions).forEach(key => {
        if (!this.columnOptions[key].unchecked) this.checkedColumns.push(key)
      })

      // check default sort
      if( this.filters.sortBy ) {
        this.options.sortBy = [this.filters.sortBy];
        this.options.sortDesc = [!!this.filters.sortDesc];
      }
      if (!this.url) {
        this.options.page = this.response.current_page || 1;
        this.options.itemsPerPage = parseInt(this.response.per_page);
        this.loading = false;
        this.firstLoad = false;
      }
    })
  }
}
</script>

<style lang="scss" scoped>
	.total-row {
		font-weight: bolder;
		font-size: larger;
	}

	.v-data-table {
		margin: 0 auto;
	}
	
	.v-data-table td {
		max-width: 100%;
		white-space: nowrap;
	}
</style>
